// The fixed core. One sound engine; every interface drives it through the
// command API in ../api.js and never touches this module directly.

import * as Tone from 'tone'
import { SOUNDS, clonePresets } from './presets.js'

export const STEPS = 16

// Tone's default lookAhead (0.1 s) is added by `Tone.now()`, so a voice fired
// with no explicit time would sound 100 ms after the finger that asked for it.
// Interactive triggers therefore use `Tone.immediate()` (raw currentTime), and
// the context is built for the shortest buffer the device will give us. The
// Transport keeps its own scheduling and is unaffected.
const INTERACTIVE_LOOKAHEAD = 0.01

export function createEngine() {
  Tone.getContext().lookAhead = INTERACTIVE_LOOKAHEAD
  const presets = clonePresets()
  const master = new Tone.Gain(0.8).toDestination()

  const voices = {}
  for (const name of SOUNDS) {
    const p = presets[name]
    const synth = new Tone.FMSynth({
      harmonicity: p.harmonicity,
      modulationIndex: p.modulationIndex,
      oscillator: { type: 'sine' },
      modulation: { type: name === 'snare' || name === 'hat' ? 'square' : 'sine' },
      envelope: { attack: p.attack, decay: p.decay, sustain: p.sustain, release: p.release },
      modulationEnvelope: { attack: 0.001, decay: p.decay * 0.8, sustain: 0, release: p.release },
    })
    const gain = new Tone.Gain(p.gain).connect(master)
    synth.connect(gain)
    voices[name] = { synth, gain }
  }

  // pattern[track][step], track index follows SOUNDS order
  const pattern = SOUNDS.map(() => new Array(STEPS).fill(false))

  const stepListeners = new Set()
  const changeListeners = new Set()
  const notify = (evt) => { for (const fn of changeListeners) fn(evt) }

  let started = false   // has Tone.start() resolved (audio unlocked)
  let playing = false

  const transport = Tone.getTransport()
  transport.bpm.value = 100

  const sequence = new Tone.Sequence(
    (time, step) => {
      for (let t = 0; t < SOUNDS.length; t++) {
        if (pattern[t][step]) fireVoice(SOUNDS[t], time)
      }
      // playhead updates must go through Draw, not straight to the DOM:
      // the callback runs ahead of real time by the lookahead amount.
      Tone.getDraw().schedule(() => {
        for (const fn of stepListeners) fn(step)
      }, time)
    },
    [...Array(STEPS).keys()],
    '16n',
  )

  // Play a voice at an audio-clock time. `time` is in AudioContext seconds.
  function fireVoice(name, time) {
    const v = voices[name]
    if (!v) return
    const p = presets[name]
    // `Tone.immediate()` is currentTime with no lookahead added: right for a
    // response to a live gesture. Sequenced notes pass an explicit `time`.
    const t = time ?? Tone.immediate()
    // Percussive character comes mostly from the pitch sweep, which FMSynth
    // has no parameter for, so ramp the carrier by hand.
    if (p.pitchDecay > 0) {
      v.synth.frequency.cancelScheduledValues(t)
      v.synth.frequency.setValueAtTime(p.frequency * 2.2, t)
      v.synth.frequency.exponentialRampToValueAtTime(p.frequency, t + p.pitchDecay)
    } else {
      v.synth.frequency.setValueAtTime(p.frequency, t)
    }
    v.synth.triggerAttackRelease(p.frequency, p.decay, t)
  }

  return {
    sounds: SOUNDS,
    steps: STEPS,

    // Audio cannot start until a user gesture. Call this from a real
    // pointer/key handler; it is safe to call more than once.
    async unlock() {
      if (started) return
      await Tone.start()
      // Safari can hand back a suspended context even after start() resolves.
      const raw = Tone.getContext().rawContext
      if (raw.state !== 'running') await raw.resume().catch(() => {})
      started = true
      notify({ type: 'unlock' })
    },

    get unlocked() { return started },
    get playing() { return playing },
    get tempo() { return transport.bpm.value },
    get lookAhead() { return Tone.getContext().lookAhead },

    trigger(name, time) {
      fireVoice(name, time)
    },

    setStep(track, step, on) {
      if (track < 0 || track >= SOUNDS.length) return
      if (step < 0 || step >= STEPS) return
      pattern[track][step] = !!on
      notify({ type: 'pattern' })
    },

    setTrack(track, steps) {
      if (track < 0 || track >= SOUNDS.length) return
      for (let i = 0; i < STEPS; i++) pattern[track][i] = !!steps[i]
      notify({ type: 'pattern' })
    },

    clear() {
      for (const row of pattern) row.fill(false)
      notify({ type: 'pattern' })
    },

    getPattern() { return pattern.map((row) => [...row]) },

    setTempo(bpm) {
      const v = Math.max(40, Math.min(240, Number(bpm) || 100))
      transport.bpm.value = v
      notify({ type: 'tempo', value: v })
    },

    start() {
      if (playing) return
      sequence.start(0)
      transport.start()
      playing = true
      notify({ type: 'transport', playing })
    },

    stop() {
      if (!playing) return
      transport.stop()
      sequence.stop()
      playing = false
      notify({ type: 'transport', playing })
    },

    setParam(sound, name, value) {
      const p = presets[sound]
      if (!p || !(name in p)) return
      p[name] = value
      const v = voices[sound]
      switch (name) {
        case 'harmonicity': v.synth.harmonicity.value = value; break
        case 'modulationIndex': v.synth.modulationIndex.value = value; break
        case 'gain': v.gain.gain.rampTo(value, 0.02); break
        case 'attack': case 'decay': case 'sustain': case 'release':
          v.synth.envelope[name] = value; break
        default: break   // frequency and pitchDecay are read at trigger time
      }
      notify({ type: 'param', sound, name, value })
    },

    getPresets() { return structuredClone(presets) },

    setMasterVolume(v) { master.gain.rampTo(v, 0.05) },

    onStep(fn) { stepListeners.add(fn); return () => stepListeners.delete(fn) },
    onChange(fn) { changeListeners.add(fn); return () => changeListeners.delete(fn) },

    dispose() {
      sequence.dispose()
      for (const v of Object.values(voices)) { v.synth.dispose(); v.gain.dispose() }
      master.dispose()
    },
  }
}
